import isEqual from "lodash/isEqual";
import { companyVoiceOwner } from "../../protocol/voice-owner";
import { resolveGroupGeometry } from "../../vendor/src/features/remotion-editor/editor/items/group/group-geometry";
import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { AudioItem } from "../../vendor/src/features/remotion-editor/editor/items/audio/audio-item-type";

const interval = (from: number, duration: number) =>
  Number.isSafeInteger(from) &&
  from >= 0 &&
  Number.isSafeInteger(duration) &&
  duration > 0 &&
  Number.isSafeInteger(from + duration);

/** A natural-length member may use existing space, never enlarge a parent or move another item. */
export function groupVoiceNaturalError(
  state: EditorState,
  id: string,
  length: number,
): string | null {
  const items = state.undoableState.items,
    old = items[id];
  if (
    old?.type !== "audio" ||
    !interval(old.from, old.durationInFrames) ||
    !interval(old.from, length)
  )
    return "음성 구간을 확인해 주세요.";
  let owner: ReturnType<typeof companyVoiceOwner>;
  try {
    owner = companyVoiceOwner(state.undoableState, id);
  } catch {
    return "그룹 연결을 확인하거나 구간 맞추기를 선택해 주세요.";
  }
  if (!owner.parentIds.length || owner.track.locked)
    return "그룹과 트랙의 잠금을 확인해 주세요.";
  const track = state.undoableState.tracks.find((t) => t.id === owner.trackId);
  if (!track) return "음성 트랙을 확인해 주세요.";
  const seen = new Set<string>(),
    peers: string[] = [];
  const visit = (key: string, depth: number): boolean => {
    if (depth > 64 || seen.size >= 10000 || seen.has(key)) return false;
    seen.add(key);
    const item = items[key];
    if (
      !item ||
      item.id !== key ||
      item.groupId != null ||
      !interval(item.from, item.durationInFrames)
    )
      return false;
    if (item.type === "audio") {
      if (key !== id) peers.push(key);
      return true;
    }
    if (item.type === "video") {
      const asset = state.undoableState.assets[item.assetId];
      if (asset?.type !== "video" || asset.id !== item.assetId) return false;
      // Match the source-voice reader: only explicit mute or a known silent
      // video is silent. Unknown audio metadata still reserves its interval.
      if (item.muteAudio !== true && asset.hasAudioTrack !== false)
        peers.push(key);
      return true;
    }
    if (
      ["image", "text", "captions", "solid", "blur", "gif", "webp"].includes(
        item.type,
      )
    )
      return true;
    // Code and embedded documents have no established audible-content contract.
    return (
      item.type === "group" &&
      item.children.length > 0 &&
      item.children.every((child) => visit(child, depth + 1))
    );
  };
  if (!track.items.every((key) => visit(key, 0)))
    return "그룹 연결이나 소리 범위를 확인할 수 없는 항목이 있습니다. 구간 맞추기나 자료함 보관을 사용해 주세요.";
  const end = old.from + length;
  if (
    peers.some((key) => {
      const item = items[key]!;
      return item.from < end && item.from + item.durationInFrames > old.from;
    })
  )
    return "새 음성이 다른 음성 또는 영상 원음과 겹칩니다. 구간 맞추기를 선택하거나 빈 공간을 확보해 주세요.";
  if (
    Object.values(state.undoableState.transitions).some(
      (t) => t.sourceItemId === id || t.targetItemId === id,
    )
  )
    return "전환이 연결된 음성은 구간 맞추기를 사용해 주세요.";
  const nextItems = { ...items, [id]: { ...old, durationInFrames: length } };
  for (const key of owner.parentIds) {
    const group = items[key];
    if (group?.type !== "group") return "부모 그룹을 확인해 주세요.";
    if (
      group.preserveGroupFrame &&
      (old.from < group.from ||
        Math.max(end, old.from + old.durationInFrames) >
          group.from + group.durationInFrames)
    )
      return "부모 그룹의 표시 구간을 벗어납니다. 구간 맞추기를 선택해 주세요.";
    const before = resolveGroupGeometry(group, items),
      after = resolveGroupGeometry(group, nextItems);
    if (
      before.from !== after.from ||
      before.durationInFrames !== after.durationInFrames
    )
      return "새 길이가 그룹의 시작이나 끝을 바꿉니다. 구간 맞추기 또는 병합 해제 후 교체를 사용해 주세요.";
  }
  return null;
}

/** Validate the exact source swap before bypassing magnetic/group timing postprocessing. */
export function isGroupNaturalVoiceUpdate(
  previous: EditorState,
  next: EditorState,
  id: string,
): boolean {
  const before = previous.undoableState,
    after = next.undoableState,
    old = before.items[id],
    item = after.items[id];
  if (
    before.assets !== after.assets ||
    before.fps !== after.fps ||
    old?.type !== "audio" ||
    item?.type !== "audio" ||
    old.assetId === item.assetId
  )
    return false;
  if (
    Object.entries(before.items).some(
      ([key, value]) => key !== id && after.items[key] !== value,
    )
  )
    return false;
  const asset = before.assets[item.assetId];
  if (
    asset?.type !== "audio" ||
    !Number.isFinite(asset.durationInSeconds) ||
    asset.durationInSeconds <= 0
  )
    return false;
  const length = Math.max(
    1,
    Math.ceil(asset.durationInSeconds * before.fps - 1e-7),
  );
  const expected: AudioItem & { speechSource?: unknown } = {
    ...old,
    assetId: item.assetId,
    durationInFrames: length,
    audioStartFromInSeconds: 0,
    playbackRate: 1,
    audioFadeWindow: undefined,
    audioFadeInDurationInSeconds: Math.min(
      old.audioFadeInDurationInSeconds,
      length / before.fps,
    ),
    audioFadeOutDurationInSeconds: Math.min(
      old.audioFadeOutDurationInSeconds,
      length / before.fps,
    ),
  };
  delete expected.speechSource;
  return (
    isEqual(expected, item) &&
    groupVoiceNaturalError(previous, id, length) === null
  );
}
